import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ActivityIndicator, Linking, TouchableOpacity, View } from "react-native"
import Animated, {
    BounceIn,
    useAnimatedStyle,
    useSharedValue,
    withRepeat,
    withTiming,
} from "react-native-reanimated"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import CameraPreview from "../../../engine/precall/CameraPreview"
import TextM from "@common/components/text/TextM"
import { PlaySoundM } from "@common/components/playSound/PlaySoundM"
import { theme } from "@common/theme/theme"
import { CAUTION, DANGER } from "./precallColors"
import { IS_WEB } from "@common/utils/mobileUtils"
import { useInterval } from "@common/utils/useInterval"
import { useRememberedCheck } from "../../../engine/precall/useRememberedCheck"
import { browserFamily } from "../../../engine/precall/verifiedMemory"
import { permissionChoiceIndex } from "../../../engine/precall/browserPermissionPaths"
import {
    CAUSE,
    IMAGE,
    STATUS,
    cameraStatus,
    cameraVerdict as computeCameraVerdict,
    microphoneStatus,
} from "../../../engine/precall/deviceCheckRules"
import {
    AUDIENCE,
    audienceCopy,
    browserLabel,
    cameraDetail,
    memoryMissDetail,
    microphoneDetail,
} from "../../../engine/precall/deviceCheckCopy"
import { useDeviceCheck } from "../../../engine/precall/useDeviceCheck"
import { useMediaDevices } from "../../../engine/precall/useMediaDevices"
import { useNetworkCheck, networkDetail } from "../../../engine/precall/useNetworkCheck"
import { useCameraObstruction } from "../../../engine/precall/useCameraObstruction"
import { ACTION, visibleActions } from "../../../engine/precall/precallActions"
import { useTroubleshooting } from "./useTroubleshooting"
import { prefersReducedMotion, useLayout } from "./precallLayout"
import { playbackStarted } from "../../../engine/precall/playbackStarted"
import StepButton, { buttonRow } from "./StepButton"
import StepRow, { DOT_COLOR, STEP_TONE } from "./StepRow"
import { MicrophoneLevel, OutputLevel } from "./LevelMeters"
import TypedGuidance, { PermissionHint } from "./TypedGuidance"
import { PRECALL_EVENT, trackPrecall } from "../../../engine/precall/precallTracking"
import {
    CAMERA_LADDER,
    MICROPHONE_LADDER,
    NETWORK_LADDER,
    SPEAKER_LADDER,
} from "../../../engine/precall/troubleshootingLadders"

/**
 * Le test matériel d'entrée au live, sous forme de parcours.
 *
 * Les quatre étapes restent listées de bout en bout, chacune avec sa phrase
 * d'état — « On t'entend », « Connexion rapide ». Une seule est dépliée : celle
 * dont c'est le tour. La checklist se lit donc d'un coup d'œil sans que trois
 * conseils se disputent l'attention.
 *
 * L'ordre va du plus facile au plus exigeant : la connexion ne demande rien,
 * le micro un mot, le son une écoute, la caméra un regard. Le test des
 * haut-parleurs est une vérification à part entière, avec son verdict.
 *
 * L'aperçu vit dans l'étape caméra, juste au-dessus de son conseil. Avoir
 * l'image d'un côté et le guidage de l'autre rendait ce guidage
 * incompréhensible.
 *
 * L'écran ne dit plus « on te voit bien » devant une image noire : l'obstruction
 * est mesurée avec la même analyse que la détection en séance, elle est dite à
 * l'élève, et tant qu'elle dure l'étape ne se valide pas.
 *
 * Aucun bouton « revérifier » : les mesures tournent en continu, la ligne passe
 * au vert d'elle-même. Seuls subsistent les gestes que l'app ne peut pas faire
 * à la place de l'élève — autoriser, écouter, activer sa vidéo.
 */

/** Ce qu'on annonce à l'élève, et pourquoi on le vérifie. */
/**
 * Le nom de chaque appareil, tel que l'élève le lit sur sa checklist — et tel
 * que le technicien le relit dans « Déjà essayé ». Une seule source : renommer
 * une étape à l'écran ne doit pas désaligner en silence le signalement.
 */
const DEVICE_LABEL = {
    network: "Connexion",
    microphone: "Micro",
    speaker: "Haut-parleurs",
    camera: "Caméra",
}

// Ce que l'élève FAIT à chaque ligne, pas pourquoi : des « Pour que… » sous
// quatre libellés se lisaient comme les champs d'un formulaire (retour Ops,
// 03/10). Le pourquoi tient en une phrase, dans l'en-tête.
const INTRO_ITEMS = [
    { id: "network", icon: "wifi", label: "Ta connexion", does: "On la mesure tout seul" },
    { id: "microphone", icon: "microphone", label: "Ton micro", does: "Tu dis « allô »" },
    { id: "speaker", icon: "volume-up", label: "Tes haut-parleurs", does: "Tu écoutes un petit son" },
    { id: "camera", icon: "video", label: "Ta caméra", does: "Tu regardes ton image" },
]

/** Durée de la mélodie de test, un peu arrondie vers le haut. */
const CHIME_MS = 700

/**
 * Le temps qu'on laisse à une réussite pour se faire voir avant d'enchaîner.
 *
 * Le micro en demande davantage, et c'est la seule étape dans ce cas : c'est
 * la seule dont la preuve est une mesure en mouvement. Le seuil de détection
 * est bas — volontairement, il s'agit de distinguer un micro coupé d'un micro
 * qui marche, pas de juger du volume —, donc l'étape peut se valider au
 * premier bruit de la pièce, avant même que l'élève ait regardé. Les barres
 * restaient alors affichées quelques dizaines de millisecondes : on lui
 * demandait de dire « allô » et de voir bouger quelque chose qu'il n'avait
 * pas le temps de voir.
 *
 * Les trois autres étapes n'ont pas cette contrainte — leur preuve (verdict
 * réseau, écoute, image) est déjà visible avant l'enchaînement — donc leur
 * délai vise seulement à laisser voir le ✓ vert, pas à laisser le temps d'une
 * mesure.
 */
const RESOLVED_MS = { microphone: 2400, default: 1300 }
const holdFor = (stepId) => RESOLVED_MS[stepId] ?? RESOLVED_MS.default

/**
 * Le temps qu'on laisse à l'élève pour dire « allô » avant de soupçonner son
 * matériel.
 *
 * Sans ce délai, le tour du micro s'ouvrait droit sur le premier barreau de
 * dépannage — « regarde l'interrupteur mute de ton casque » — parce que ce
 * barreau s'applique dès que l'accès est accordé. On ne lui demandait donc
 * jamais simplement de parler : la phrase qui le fait était devenue
 * inatteignable, et il se voyait soupçonné d'un micro coupé avant qu'on ait
 * écouté quoi que ce soit.
 *
 * Douze secondes, et pas sept : mesuré au navigateur, la phrase met près de six
 * secondes à s'écrire. À sept, l'élève finissait tout juste de la lire quand le
 * dépannage lui coupait la parole.
 */
const MIC_GRACE_MS = 12000

/**
 * Ce que le souvenir couvre, et donc ce qu'il faut avoir mesuré pour l'écrire.
 *
 * Une seule liste, parce que les deux lectures doivent s'accorder : ce qu'on
 * dispense de rejouer et ce qu'on exige d'avoir vu au vert. La connexion n'y
 * est pas — elle est remesurée à chaque entrée.
 */
const REMEMBERED_STEPS = ["microphone", "speaker", "camera"]

/** Ce qu'on dit à l'élève au moment précis où une étape se règle. */
const RESOLVED_TEXT = {
    network: "Ta connexion est bonne.",
    microphone: "On t'entend, c'est parfait.",
    speaker: "Tu entends bien, c'est parfait.",
    camera: "On te voit, c'est parfait.",
}

const PreCallCheckPanel = ({
    style,
    isVideoMuted,
    onSetVideoMuted,
    onStateChange,
    onDevicesChange,
    /** `{ signature, at }` du dernier parcours réussi sur cet appareil, ou null. */
    verified = null,
    /** Appelé avec l'empreinte du jour quand toutes les étapes sont mesurées bonnes. */
    onVerified,
    /** Contexte commun des événements Amplitude : `live_session_id`, `platform`, `browser`. */
    tracking = {},
    /** Qui est devant l'écran : décide qui est « de l'autre côté » dans les textes. */
    audience = AUDIENCE.STUDENT,
}) => {
    const copy = audienceCopy(audience)
    // Une seule lecture du contexte : les deux valeurs sortent du même seuil.
    const { compact, indent } = useLayout()
    const { causes, isChecking, run } = useDeviceCheck()
    const micGranted = causes.microphone === CAUSE.GRANTED
    const { devices, refresh: refreshDevices } = useMediaDevices()
    const network = useNetworkCheck()

    const [audioDeviceId, setAudioDeviceId] = useState(null)
    const [videoDeviceId, setVideoDeviceId] = useState(null)
    // La caméra choisie n'a pas répondu. Fréquent avec les caméras virtuelles
    // — OBS et consorts — que le système liste même quand elles ne diffusent
    // rien : sans ce retour, le clic sur l'autre caméra semblait sans effet.
    const [cameraDeviceError, setCameraDeviceError] = useState(null)

    /*
     * Une image a-t-elle déjà été affichée ?
     *
     * Le repli de l'aperçu avait été écrit pour un *changement* d'objectif
     * raté : là, l'ancien flux tourne encore, et « tu vois toujours la
     * précédente » est vrai. Sur une machine sans caméra, la toute première
     * ouverture échoue — il n'y a jamais eu de précédente, et la phrase parlait
     * d'une image qui n'a jamais existé, au-dessus d'un cadre noir.
     *
     * L'aperçu web est le seul à savoir : il rappelle `onDeviceError(null)`
     * quand un flux arrive. En natif il affiche la caméra lui-même et ne rend
     * jamais d'erreur — rien ne change pour lui.
     */
    const [previewEverOpened, setPreviewEverOpened] = useState(false)
    const previewShowsImage = !IS_WEB || previewEverOpened
    const [speakerHeard, setSpeakerHeard] = useState(null) // null = pas encore testé
    const [soundPlayed, setSoundPlayed] = useState(false)
    const [chiming, setChiming] = useState(false)
    // Constaté, pas supposé : la lecture a été lancée et rien n'est sorti.
    const [soundBlocked, setSoundBlocked] = useState(false)

    /*
     * Un seul verdict de lecture en vol à la fois. Deux appuis rapprochés
     * laissaient tourner le minuteur du premier : il éteignait les barres du
     * second, et jugeait la seconde écoute sur la piste de la première.
     */
    /*
     * Seul le dernier appui a le droit de conclure. L'observation dure le
     * temps de la mélodie : sans ce jeton, le verdict d'une première écoute
     * viendrait écraser celui d'une seconde, lancée entre-temps.
     */
    const chimeRun = useRef(0)
    useEffect(() => () => { chimeRun.current += 1 }, [])
    // Confirmation visuelle de l'élève — là seulement où rien n'a lu l'image :
    // sur natif, ou sur le web quand l'analyse a renoncé.
    const [seesSelf, setSeesSelf] = useState(null)
    const [skipped, setSkipped] = useState({})
    // Rien ne commence tant que l'élève n'a pas dit qu'il commençait.
    const [started, setStarted] = useState(false)

    /*
     * Le souvenir du dernier parcours réussi sur cet appareil.
     *
     * La vérification silencieuse tourne au montage : elle relit la
     * configuration sans rien ouvrir, et dit si elle correspond. `forceFull`
     * est la porte de sortie, pour l'élève qui veut revérifier quand même.
     */
    /*
     * Le guidage des permissions dépend du navigateur : chacun montre une
     * icône différente, à un endroit différent, et Safari n'en montre aucune.
     */
    const browser = IS_WEB && typeof navigator !== "undefined"
        ? browserFamily(navigator.userAgent)
        : "autre"

    /*
     * La mesure. Le contexte passe par une référence : le parent le construit
     * à chaque rendu, et en faire une dépendance relancerait les effets.
     * `lastRung` retient le dernier barreau montré par étape, pour dire au
     * moment où elle se règle ou se passe lequel a été le dernier essayé.
     */
    const trackingRef = useRef(tracking)
    trackingRef.current = tracking
    const track = useCallback(
        (event, properties) => trackPrecall(event, { ...trackingRef.current, ...properties }),
        []
    )
    const lastRungRef = useRef({})

    const memory = useRememberedCheck(verified)
    const [forceFull, setForceFull] = useState(false)
    const memoryApplies = memory.matches && !forceFull

    // Le fait retenu, pas la mesure : une fois le micro entendu, il l'est pour
    // le reste du parcours, même quand l'étape est repliée et que plus rien
    // n'écoute.
    const [micHeard, setMicHeard] = useState(false)
    const handleMicHeard = useCallback(() => setMicHeard(true), [])
    useEffect(() => setMicHeard(false), [causes.microphone])

    /*
     * On écoute avant de suspecter. Le dépannage du micro ne commence qu'une
     * fois ce délai passé sans avoir rien entendu — d'ici là, l'écran demande
     * simplement de parler.
     */
    const [micGraceOver, setMicGraceOver] = useState(false)

    const needsAuthorization =
        causes.camera === CAUSE.NOT_ASKED || causes.microphone === CAUSE.NOT_ASKED
    // La mesure réseau n'entre pas ici : elle grisait le bouton « Autoriser »
    // pendant toute sa durée — jusqu'à douze secondes sur un lien mort — pour
    // une raison qui n'a rien à voir avec les permissions.
    const busy = isChecking

    const networkVerdict = network.status

    /*
     * L'attente de la vérification silencieuse.
     *
     * Sans elle, l'écran d'accueil s'affiche puis se fait remplacer par la
     * carte : l'élève voit clignoter l'écran qu'on lui annonce justement lui
     * épargner.
     */
    const probing = !started && !memory.ready

    const microphoneVerdict = microphoneStatus(causes.microphone, micHeard)
    /*
     * Le micro n'est MESURÉ que quand on l'a écouté.
     *
     * Chez un élève déjà autorisé, la sonde de montage remonte « accordé »
     * avant tout clic, et un micro accordé sans son vaut ⚠️ à l'affichage. Ce
     * ⚠️ partait tel quel vers la règle d'alerte : « Entrer sans finir »
     * depuis l'accueil pinguait l'équipe pour un micro jamais écouté. Vers le
     * parent, un micro accordé qu'on n'a ni entendu ni attendu vaut donc « pas
     * encore testé » ; un refus, une absence ou une occupation restent des
     * mesures, elles.
     */
    const micMeasured = causes.microphone !== CAUSE.GRANTED || micHeard || micGraceOver
    const reportedMicrophoneVerdict = micMeasured ? microphoneVerdict : STATUS.PENDING
    const speakerVerdict =
        speakerHeard === true ? STATUS.OK : speakerHeard === false ? STATUS.WARN : STATUS.PENDING

    // Verdict caméra : l'ouverture du flux ne suffit pas. Une image noire doit
    // se dire, et empêcher la validation.
    const cameraOpened = cameraStatus(causes.camera)

    /**
     * Le tour de la caméra est-il venu ? Les trois étapes qui la précèdent ne
     * dépendent pas d'elle : on peut le savoir sans consulter son verdict, donc
     * sans boucle.
     *
     * C'est l'aperçu qui allume la webcam, et il n'est monté qu'à l'étape
     * caméra : l'allumer plus tôt ferait briller la diode sans que rien à
     * l'écran ne l'explique. L'analyse d'obstruction, elle, n'ouvre plus rien
     * du tout — elle lit les images de cet aperçu.
     */
    const cameraStepReached =
        (networkVerdict === STATUS.OK || skipped.network) &&
        (microphoneVerdict === STATUS.OK || skipped.microphone) &&
        (speakerVerdict === STATUS.OK || skipped.speaker)

    // L'analyse lit les images de l'aperçu affiché juste au-dessus du conseil,
    // plutôt que d'ouvrir un second flux sur la même webcam.
    const previewRef = useRef(null)
    /*
     * L'aperçu paraît aussi sur la carte « déjà vérifié », donc l'analyse
     * tourne aussi là — et ce n'est pas de la décoration. Montrer une image
     * sans la lire reviendrait à afficher un carré noir sous « tout est
     * vérifié » : l'élève y verrait la contradiction avant nous.
     */
    const analysingImage =
        IS_WEB && !isVideoMuted && (memoryApplies || (cameraOpened === STATUS.OK && cameraStepReached))
    const { isObstructed, image } = useCameraObstruction({
        enabled: analysingImage,
        previewRef,
    })

    /*
     * Ce que le souvenir dispense de rejouer — et rien de plus.
     *
     * Les trois étapes listées ici sont celles que seul l'élève peut juger :
     * est-ce qu'on t'entend, est-ce que tu entends, est-ce que tu te vois. Ce
     * sont les seules qui coûtent du temps et de l'attention, et les seules
     * dont la réponse dépend d'un matériel qui n'a pas changé.
     *
     * La connexion n'y est délibérément pas : elle varie tous les jours sans
     * que le matériel bouge, et la mesurer ne demande aucun geste. Elle est
     * donc remesurée à chaque entrée, y compris ici — si elle cloche ce
     * jour-là, la file ouvrira l'étape connexion, et elle seule.
     *
     * L'aperçu de la carte est un MIROIR, pas un examen.
     *
     * Une version antérieure lui laissait invalider le souvenir : image noire
     * ou caméra occupée, et l'écran basculait sur l'étape caméra. C'était
     * incohérent de bout en bout — on annonçait à l'élève qu'il n'avait rien à
     * faire, puis on lui retirait l'écran deux secondes plus tard, et il
     * fallait retarder la carte le temps de juger l'image pour éviter le
     * clignotement. Un écran qui se reprend vaut moins qu'un écran qui ne
     * promet rien de trop.
     *
     * L'image reste analysée, et la pastille « image toute noire » se pose sur
     * l'aperçu — ça suffit à le dire. Mais c'est l'élève qui tranche en se
     * regardant, ce qu'aucune heuristique ne fait mieux que lui.
     */
    const isRemembered = (id) => memoryApplies && REMEMBERED_STEPS.includes(id)

/**
     * Le verdict ACQUIS d'une étape : ce qu'on sait d'elle, mesuré aujourd'hui
     * ou retenu d'un parcours précédent. À ne pas confondre avec le verdict
     * mesuré, qui reste seul dans `diagnostics` — un technicien a besoin de
     * savoir ce qui a été constaté, pas ce qui est réputé bon.
     */
    const acquired = (id, verdict) => (isRemembered(id) ? STATUS.OK : verdict)

    /*
     * Ce que l'analyse a fini par dire, et qui ne se dédit pas.
     *
     * Rouvrir une étape précédente coupe l'analyse, qui repart à « je regarde ».
     * Sans ce verrou, l'élève serait renvoyé refaire son test caméra, ou une
     * caméra validée par l'analyse redeviendrait une question quand l'aperçu se
     * démonte. Un verdict l'emporte sur un renoncement : sur Windows, l'image
     * arrive souvent après le délai, et ce verdict tardif est le bon.
     */
    const [imageSeen, setImageSeen] = useState(IMAGE.LOOKING)
    useEffect(() => {
        if (image === IMAGE.LOOKING) return
        setImageSeen((seen) => (seen === IMAGE.JUDGED ? seen : image))
    }, [image])

    const cameraVerdict = useMemo(
        () =>
            computeCameraVerdict({
                isVideoMuted,
                opened: cameraOpened,
                isObstructed,
                image: imageSeen,
                seesSelf,
                isWeb: IS_WEB,
            }),
        [isVideoMuted, cameraOpened, isObstructed, imageSeen, seesSelf]
    )

    /**
     * La phrase d'état d'une étape, quelle qu'elle soit — pas seulement celle en
     * cours. C'est elle qui reste affichée sur chaque ligne du parcours.
     */
    const detailOf = (id) => {
        if (id === "network") return networkDetail(network.latency)
        // Ce que le souvenir couvre se lit comme tel, plutôt que « pas encore
        // testé » — qui serait faux — ou « on t'entend » — qu'on n'a pas
        // mesuré aujourd'hui.
        if (isRemembered(id)) return "Vérifié la dernière fois"
        // Un micro accordé qu'on n'a pas encore écouté n'est pas « muet » : la
        // ligne disait « Je n'entends rien » pendant l'étape connexion.
        if (id === "microphone" && !micMeasured) return "Pas encore testé"
        if (id === "microphone") return microphoneDetail(causes.microphone, IS_WEB, micHeard)
        if (id === "speaker") {
            if (speakerHeard === true) return "Tu entends bien"
            if (speakerHeard === false) return "Tu n'entends rien"
            return "Pas encore testé"
        }
        // Garde explicite : sans elle, la suite servait de fourre-tout à tout
        // identifiant inconnu, et un cinquième appareil aurait hérité en
        // silence des phrases de la caméra.
        if (id !== "camera") return ""
        // Vidéo coupée : sans flux, aucune des phrases suivantes n'est vraie.
        // La ligne retombait sur « On te voit bien » au-dessus d'un aperçu noir.
        if (isVideoMuted) return "Ta vidéo est coupée"
        if (isObstructed) return "Quelque chose cache la caméra"
        // « Je regarde ton image » ne se dit que si on la regarde vraiment :
        // l'analyse ne tourne qu'au tour de la caméra, faute d'aperçu à lire
        // avant, et la ligne l'annonçait pourtant dès la première étape.
        if (analysingImage && imageSeen === IMAGE.LOOKING) return "Je regarde ton image…"
        // La phrase de la cause sert deux fois dans cette branche, à deux
        // titres : ici pour dire « On te voit » à une caméra validée, et en fin
        // de branche pour dire pourquoi le flux ne s'ouvre pas.
        const cameraSentence = cameraDetail(causes.camera, IS_WEB)
        // Validée — par l'analyse ou par l'élève. Testé avant « Regarde ton
        // image », qui ne vaut que tant que la question est posée.
        if (cameraVerdict === STATUS.OK) return cameraSentence
        // « Pas encore vérifiée » ne se dit pas d'une caméra déjà validée : au
        // retour du bilan par « Réessayer », l'étape n'est plus atteinte alors
        // que son verdict, lui, est resté vert.
        if (!cameraStepReached && cameraOpened === STATUS.OK) return "Pas encore vérifiée"
        // Plus de garde sur `isVideoMuted` : la branche ci-dessus rend déjà la
        // main dans ce cas, la retester ici ne pouvait plus être faux.
        if (cameraOpened === STATUS.OK && seesSelf === null) {
            return "Regarde ton image"
        }
        if (seesSelf === false) return "Tu ne te vois pas bien"
        return cameraSentence
    }

    // ---- le parcours ----

    /**
     * Du plus facile au plus exigeant : la connexion ne demande rien à l'élève,
     * le micro un mot, le son un clic et une écoute, la caméra un regard et un
     * jugement. La caméra ferme donc la marche.
     */
    const STEPS = [
        { id: "network", icon: "wifi", label: DEVICE_LABEL.network, verdict: networkVerdict },
        { id: "microphone", icon: "microphone", label: DEVICE_LABEL.microphone, verdict: microphoneVerdict },
        { id: "speaker", icon: "volume-up", label: DEVICE_LABEL.speaker, verdict: speakerVerdict },
        { id: "camera", icon: "video", label: DEVICE_LABEL.camera, verdict: cameraVerdict },
    ].map((step) => ({
        ...step,
        detail: detailOf(step.id),
        /*
         * Le verdict que l'AFFICHAGE doit lire.
         *
         * Une étape couverte par le souvenir dit « Vérifié la dernière fois »
         * sur sa ligne, mais gardait une pastille grise et ne comptait pas dans
         * « n sur 4 » : l'en-tête annonçait « 0 sur 4 » au-dessus de trois
         * lignes vérifiées. `verdict` reste la mesure du jour — c'est lui que
         * lisent le dépannage et `diagnostics`.
         */
        acquiredVerdict: acquired(step.id, step.verdict),
    }))

    /*
     * Trois façons pour une étape d'être derrière soi : mesurée bonne
     * aujourd'hui, passée par l'élève, ou couverte par le souvenir. La file
     * ouvre « la première qui ne l'est pas » — c'est ce qui fait que, lorsque
     * seule la connexion cloche, elle est la seule à s'ouvrir, sans rien
     * ajouter à cet enchaînement.
     */
    // Le verdict acquis porte déjà le souvenir : `acquiredVerdict === OK` dit
    // exactement « mesurée bonne aujourd'hui OU couverte par le souvenir ».
    const isValidated = (step) => step.acquiredVerdict === STATUS.OK || skipped[step.id]
    const openIndex = STEPS.findIndex((step) => !isValidated(step))

    /*
     * Ce qui reste à faire malgré le souvenir, et ce qu'il faut attendre avant
     * de le dire.
     *
     * Un seul verdict est attendu : la connexion, qui se remesure toujours et
     * décide s'il reste une étape. L'image n'entre pas dans ce calcul — voir
     * `REMEMBERED_STEPS` : l'aperçu de la carte est un miroir, pas un examen, et
     * le faire trancher revenait à retarder la carte puis à la reprendre.
     *
     * Ce qui reste ouvert est une valeur DÉRIVÉE, pas un verrou : le figer en
     * état rendrait le basculement définitif, y compris sur une condition
     * d'une fraction de seconde.
     *
     * Déclaré ICI, juste après `openIndex` : lu plus bas par le compte rendu
     * remonté au parent, il tombait en zone morte quand il vivait plus loin.
     */
    const memoryVerdictsPending = memoryApplies && networkVerdict === STATUS.PENDING
    const memoryLeavesWork = memoryApplies && !memoryVerdictsPending && openIndex !== -1
    const journeyVisible = started || memoryLeavesWork
    const openId = openIndex === -1 ? null : STEPS[openIndex].id

    /*
     * Le compte à rebours d'écoute ne court que pendant le tour du micro, une
     * fois le test commencé — et il repart de zéro si l'accès change : un
     * casque rebranché mérite qu'on réécoute avant de le suspecter à nouveau.
     *
     * `started` n'est pas une précaution de style. L'étape micro devient
     * « ouverte » dès que la mesure réseau aboutit, c'est-à-dire une seconde
     * après le chargement de la page — donc pendant que l'écran d'accueil est
     * encore affiché. Sans cette condition, le délai s'écoulait avant que
     * l'élève ait cliqué « Vérifier mon matériel », et le dépannage l'accueillait
     * au bout de quelques secondes.
     */
    useEffect(() => {
        if (!started || openId !== "microphone" || !micGranted || micHeard) return undefined
        setMicGraceOver(false)
        const timer = setTimeout(() => setMicGraceOver(true), MIC_GRACE_MS)
        return () => clearTimeout(timer)
    }, [started, openId, micGranted, micHeard, causes.microphone])

    /**
     * L'étape qui vient de réussir garde la main une seconde.
     *
     * Sans ça, elle disparaît dans la liste à l'instant même où elle se règle :
     * l'élève branche son casque, dit « allô », et tout se valide d'un bloc
     * sans que rien ne le lui dise : l'app a capté le signal et n'a rien
     * montré. Le parcours s'arrête donc un instant sur la bonne nouvelle
     * avant de passer à la suite.
     */
    const [heldId, setHeldId] = useState(null)
    const previousOpenRef = useRef(openId)

    useEffect(() => {
        const previous = previousOpenRef.current
        previousOpenRef.current = openId
        if (!previous || previous === openId) return undefined

        /*
         * Rien à célébrer hors du parcours.
         *
         * Sur la carte « déjà vérifié », l'étape connexion se résout toute
         * seule pendant que l'écran attend : sans cette garde on la retenait
         * une seconde et demie, et pendant ce temps l'écran se croyait
         * inachevé — l'entrée restait le lien discret « entrer sans finir le
         * test » au lieu du bouton.
         */
        if (!started) return undefined

        // Seulement quand l'étape s'est réellement résolue. Une étape passée
        // n'a rien à célébrer, et c'est justement la confusion qu'on corrige.
        const settled = STEPS.find((step) => step.id === previous)
        if (settled?.verdict !== STATUS.OK || skipped[previous]) return undefined

        // Réglée après un dépannage : c'est ce barreau-là qui a marché.
        if (lastRungRef.current[previous]) {
            track(PRECALL_EVENT.RUNG_RESULT, {
                step: previous,
                rung: lastRungRef.current[previous],
                result: "resolved",
            })
        }
        setHeldId(previous)
        const timer = setTimeout(() => setHeldId(null), holdFor(previous))
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openId])

    const currentIndex = heldId ? STEPS.findIndex((step) => step.id === heldId) : openIndex
    const current = currentIndex === -1 ? null : STEPS[currentIndex]

    /**
     * On attend que l'élève parle — et c'est le seul geste qu'on lui demande.
     *
     * Hissé ici parce que l'animation qui l'accompagne a besoin de hooks, et
     * que `micListening` s'en sert aussi pour décider des boutons.
     */
    const askToSpeak =
        current?.id === "microphone" && micGranted && !micHeard && !micGraceOver

    /*
     * La pastille respire tant qu'on écoute.
     *
     * Un écran figé ne dit pas qu'il attend quelque chose. Ce battement est le
     * seul mouvement de la carte : il désigne la consigne sans rien ajouter à
     * lire. Coupé si le système demande à limiter les animations.
     */
    const pulse = useSharedValue(1)
    useEffect(() => {
        if (!askToSpeak || prefersReducedMotion()) {
            pulse.value = 1
            return
        }
        // Un souffle, pas un clignotement : à peine perceptible, mais suffisant
        // pour qu'un écran figé cesse de l'être.
        pulse.value = withRepeat(withTiming(1.09, { duration: 1100 }), -1, true)
    }, [askToSpeak, pulse])
    const pulseStyle = useAnimatedStyle(() => ({ transform: [{ scale: pulse.value }] }))

    const micLadder = useTroubleshooting({
        ladder: MICROPHONE_LADDER,
        cause: causes.microphone,
        isWeb: IS_WEB,
        deviceCount: devices.audioinput.length,
        isResolved: microphoneVerdict === STATUS.OK,
    })
    const camLadder = useTroubleshooting({
        ladder: CAMERA_LADDER,
        cause: isObstructed ? CAUSE.DARK_IMAGE : causes.camera,
        isWeb: IS_WEB,
        deviceCount: devices.videoinput.length,
        isVideoMuted,
        isResolved: cameraVerdict === STATUS.OK,
    })
    /**
     * Les haut-parleurs ont désormais leur échelle, comme les trois autres.
     * Sans elle, l'élève qui répondait « je n'entends rien » lisait une phrase
     * unique et définitive : répondre une seconde fois ne changeait rien, et la
     * seule issue était « Passer ». Une étape sans essais est une impasse.
     */
    const spkLadder = useTroubleshooting({
        ladder: SPEAKER_LADDER,
        cause: speakerHeard === false ? "unheard" : "untested",
        isWeb: IS_WEB,
        deviceCount: 0,
        isResolved: speakerVerdict === STATUS.OK,
    })
    const netLadder = useTroubleshooting({
        ladder: NETWORK_LADDER,
        cause: networkVerdict,
        isWeb: IS_WEB,
        deviceCount: 0,
        isResolved: networkVerdict === STATUS.OK,
    })

    // ---- re-vérification automatique ----

    /*
     * Deux verrous distincts, parce que les deux sondes visent des ressources
     * disjointes. Un drapeau unique les sérialisait pour rien : tant que le
     * réseau mesurait — jusqu'à douze secondes sur un lien mort — la
     * re-vérification des appareils était sautée, et réciproquement.
     */
    const deviceBusyRef = useRef(false)
    deviceBusyRef.current = isChecking

    /**
     * Quel appareil re-sonder en boucle, s'il y en a un.
     *
     * Deux restrictions par rapport à la première version, qui rouvrait micro ET
     * caméra toutes les cinq secondes dès qu'on était sur l'une des deux étapes.
     *
     * Un seul appareil : celui de l'étape en cours. Sonder les deux allumait la
     * webcam pendant l'étape micro.
     *
     * Et seulement s'il est en défaut. Rouvrir un appareil qui répond déjà
     * n'apprend rien — ce qui peut encore changer, le niveau sonore et
     * l'obstruction, est mesuré en continu par ailleurs, sur des flux déjà
     * ouverts. Ça évite surtout qu'un second consommateur vise la même caméra
     * que l'aperçu : sur les configurations qui refusent l'accès concurrent,
     * la sonde échouait en « périphérique occupé » et faisait virer le verdict
     * au rouge alors que l'aperçu fonctionnait sous les yeux de l'élève.
     */
    const deviceToRecheck =
        (current?.id === "microphone" || current?.id === "camera") &&
        causes[current.id] !== CAUSE.GRANTED
            ? current.id
            : null

    // Le navigateur ne nomme les périphériques qu'une fois l'accès accordé :
    // avant, la liste existe mais les libellés sont vides. On la rafraîchit donc
    // au moment où une autorisation change, sinon le sélecteur proposerait
    // « Appareil 1 » et « Appareil 2 » — et c'est justement quand l'accès est
    // accordé que ce sélecteur apparaît.
    useEffect(() => {
        refreshDevices()
    }, [causes.microphone, causes.camera, refreshDevices])

    /*
     * Les deux boucles passent par `useInterval`, qui garde la fonction dans une
     * référence : le minuteur ne dépend plus de ce que la fonction capture.
     * Écrites avec un `setInterval` nu, elles se faisaient détruire et recréer à
     * chaque rendu — soit dix fois par seconde — et la boucle réseau n'atteignait
     * jamais ses neuf secondes. `null` en délai suspend la boucle.
     */
    useInterval(() => {
        if (!deviceBusyRef.current) {
            run({ request: false, audioDeviceId, videoDeviceId, only: deviceToRecheck })
        }
    }, deviceToRecheck ? 5000 : null)

    /*
     * Le périphérique choisi ici doit suivre l'élève en séance.
     *
     * Sans ça, le sélecteur ne réparait rien : on lui faisait trouver le bon
     * micro, puis la salle rouvrait celui du système. La remontée passe par une
     * référence, comme le reste de l'état remonté au parent, parce qu'une
     * fonction recréée à chaque rendu suffirait à faire boucler l'effet.
     */
    const devicesRef = useRef(onDevicesChange)
    devicesRef.current = onDevicesChange
    useEffect(() => {
        devicesRef.current?.({ audioinput: audioDeviceId, videoinput: videoDeviceId })
    }, [audioDeviceId, videoDeviceId])

    /*
     * La boucle réseau ne tourne que pendant l'étape réseau.
     *
     * Sa condition d'arrêt ne regardait que le verdict, et elle s'arrête quand
     * tout va bien — donc elle ne guette pas une dégradation, seulement une
     * amélioration. Or ça n'a de sens que pendant l'étape où l'élève agit sur
     * son wifi. Sans cette garde, un élève à 400 ms continuait de sonder
     * pendant le micro, le son, la caméra et jusqu'après le bilan : trois
     * requêtes toutes les neuf secondes, sur le lien même qu'on s'apprête à
     * confier à LiveKit, chez ceux qui peuvent le moins se le permettre.
     */
    useInterval(
        () => {
            if (!network.isMeasuring) network.measure()
        },
        current?.id === "network" && networkVerdict !== STATUS.PENDING ? 9000 : null
    )

    useEffect(() => {
        if (!IS_WEB || typeof window === "undefined") return undefined
        /*
         * Le retour dans l'onglet ne rouvre que ce qu'on ne sait pas déjà.
         *
         * Ce passage appelait `run` sans `only`, donc il rouvrait micro ET
         * caméra — la restriction si soigneusement posée sur la boucle 5 s ne
         * s'appliquait pas ici. Pour tout élève déjà autorisé, c'est-à-dire
         * tout le monde dès la deuxième séance, chaque retour dans l'onglet
         * rallumait la webcam pour une information connue. Et pendant l'étape
         * caméra, c'était un second pipeline de capture sur la webcam que
         * l'aperçu tient déjà : la contention que tout le reste du fichier
         * s'emploie à éviter. Les retours d'onglet sont fréquents ici — après
         * la fenêtre d'autorisation, après un détour par les réglages son,
         * après avoir branché un casque.
         */
        const onFocus = () => {
            if (deviceBusyRef.current || !deviceToRecheck) return
            run({ request: false, audioDeviceId, videoDeviceId, only: deviceToRecheck })
        }
        window.addEventListener("focus", onFocus)
        return () => window.removeEventListener("focus", onFocus)
    }, [run, audioDeviceId, videoDeviceId, deviceToRecheck])

    /*
     * On ne remonte que ce dont l'écran a besoin : le verdict micro et les
     * essais déjà faits, pour le filet technicien ; et le fait que le parcours
     * soit terminé, qui décide de la place du bouton d'entrée. Les autres
     * champs remontés au départ n'avaient aucun lecteur, et chacun élargissait
     * le tableau de dépendances d'un effet où un objet recréé à chaque rendu
     * suffit à provoquer une boucle infinie.
     *
     * `attemptsKey` sert de dépendance parce qu'un tableau change d'identité à
     * chaque rendu ; la valeur transmise reste le tableau lui-même.
     */
    const stateRef = useRef(onStateChange)
    stateRef.current = onStateChange
    /*
     * Ce que l'élève a tenté, sur TOUS les appareils.
     *
     * Seule l'échelle micro était lue : une échelle réseau ou caméra parcourue
     * jusqu'au bout arrivait vide chez le technicien, qui refaisait le chemin
     * déjà fait. Les libellés de barreaux ne portent pas leur appareil
     * (« autorisation » existe pour le micro comme pour la caméra), d'où le
     * préfixe. Borné à dix, ce que l'API accepte.
     */
    const attempts = useMemo(
        () =>
            [
                [DEVICE_LABEL.microphone, micLadder.attempts],
                [DEVICE_LABEL.speaker, spkLadder.attempts],
                [DEVICE_LABEL.camera, camLadder.attempts],
                [DEVICE_LABEL.network, netLadder.attempts],
            ]
                .flatMap(([device, tried]) => tried.map((label) => `${device} : ${label}`))
                .slice(0, 10),
        [micLadder.attempts, spkLadder.attempts, camLadder.attempts, netLadder.attempts]
    )
    const attemptsKey = attempts.join("|")

    useEffect(() => {
        stateRef.current?.({
            /*
             * Les quatre verdicts, et plus seulement le micro : depuis que
             * l'alerte se déclenche sur n'importe quel voyant non vert, le
             * parent a besoin de tous pour appliquer la règle.
             *
             * Ce sont les verdicts ACQUIS, pas les verdicts mesurés
             * aujourd'hui. Une étape couverte par le souvenir n'a pas été
             * rejouée, donc sa mesure du jour vaut « pas encore testé » — la
             * remonter telle quelle ferait partir une alerte à chaque entrée
             * par le chemin court, pour un matériel dont on sait justement
             * qu'il fonctionne. `diagnostics`, juste en dessous, garde la
             * mesure brute : c'est elle qui sert au dépannage.
             */
            microphone: acquired("microphone", reportedMicrophoneVerdict),
            speaker: acquired("speaker", speakerVerdict),
            camera: acquired("camera", cameraVerdict),
            network: acquired("network", networkVerdict),
            attempts,
            // `currentIndex` vaut déjà -1 quand le souvenir couvre les trois
            // étapes et que la connexion est bonne : l'entrée reprend sa forme
            // de bouton sans que l'élève ait rien eu à faire. Mais pas tant
            // qu'un verdict est en vol — le bouton apparaîtrait pour se
            // rétracter deux secondes plus tard.
            allDone: currentIndex === -1 && !memoryVerdictsPending,
            // Le parent change le libellé du lien d'évitement selon qu'une
            // vérification a commencé ou pas.
            started,
            /*
             * La CAUSE, et pas seulement le verdict.
             *
             * Le filet écrase quatre situations très différentes en un seul
             * rouge : accès refusé, blocage du système, aucun micro branché,
             * périphérique occupé par un autre logiciel. L'écrasement avait
             * lieu avant l'envoi, si bien que l'équipe pingée lisait « entrée
             * sans micro fonctionnel » sans savoir laquelle traiter — un refus
             * de permission se règle en dix secondes, un casque cassé demande
             * quelqu'un. Le champ existe et est déjà validé côté serveur.
             */
            diagnostics: {
                microphone: { status: microphoneVerdict, cause: causes.microphone },
                // Les haut-parleurs manquaient au dossier : le technicien
                // lisait « Audio » sans savoir si c'était le micro ou la
                // sortie. Pas de cause ici — c'est l'élève qui tranche, et son
                // verdict le dit déjà.
                speaker: { status: speakerVerdict },
                camera: { status: cameraVerdict, cause: causes.camera },
                network: { status: networkVerdict, latency_ms: network.latency },
            },
        })
        /*
         * `speakerVerdict` en dépendance, et ce n'est pas de la complétude.
         *
         * Répondre « Non, je n'entends rien » le fait passer en ⚠️ sans que
         * `currentIndex` ni `attemptsKey` bougent — l'étape reste ouverte. Sans
         * cette dépendance, l'effet ne se rejouait pas et le parent gardait
         * `speaker: pending`. Tant que seul le micro comptait, la valeur
         * périmée était inoffensive ; depuis que l'alerte lit les quatre
         * voyants, un haut-parleur muet ne prévenait plus personne — et un
         * `pending` ne déclenche rien, par construction.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        started,
        microphoneVerdict,
        reportedMicrophoneVerdict,
        speakerVerdict,
        cameraVerdict,
        networkVerdict,
        attemptsKey,
        currentIndex,
        memoryVerdictsPending,
    ])

    // ---- mesure Amplitude ----

    // L'écran s'affiche : par le chemin court, ou par le parcours.
    useEffect(() => {
        // `memory_miss` dit pourquoi le chemin court ne s'applique pas : sans
        // lui, un taux de raccourci en baisse ne se distingue pas d'un souvenir
        // qu'on n'écrit plus.
        if (memory.ready) {
            track(PRECALL_EVENT.OPENED, {
                memory_hit: memory.matches,
                memory_miss: memory.reason,
                // Diagnostic uniquement : quel morceau de l'empreinte diffère
                // (browser/microphone/camera), jamais un libellé de matériel.
                // Vide quand le souvenir comparé n'a pas encore ce détail.
                ...(memory.changedParts?.length ? { memory_miss_detail: memory.changedParts.join(",") } : {}),
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memory.ready])

    // Un verdict mesuré par appareil, à chaque changement — jamais « en attente ».
    useEffect(() => {
        if (networkVerdict === STATUS.PENDING) return
        track(PRECALL_EVENT.STEP_VERDICT, { step: "network", verdict: networkVerdict, latency_ms: network.latency })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [networkVerdict])
    useEffect(() => {
        if (reportedMicrophoneVerdict === STATUS.PENDING) return
        track(PRECALL_EVENT.STEP_VERDICT, { step: "microphone", verdict: reportedMicrophoneVerdict, cause: causes.microphone })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportedMicrophoneVerdict])
    useEffect(() => {
        if (speakerVerdict === STATUS.PENDING) return
        track(PRECALL_EVENT.STEP_VERDICT, { step: "speaker", verdict: speakerVerdict })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [speakerVerdict])
    useEffect(() => {
        if (cameraVerdict === STATUS.PENDING) return
        track(PRECALL_EVENT.STEP_VERDICT, { step: "camera", verdict: cameraVerdict, cause: causes.camera })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cameraVerdict])

    /*
     * On ne se souvient que d'un parcours réellement réussi.
     *
     * Pas « terminé » : terminé inclut les étapes passées, et une étape passée
     * n'est pas une étape vérifiée. Mémoriser un matériel qu'on n'a pas su
     * vérifier reviendrait à en dispenser demain — c'est exactement le
     * souvenir qui ment, et le seul défaut que ce lot ne peut pas se permettre.
     *
     * On lit les verdicts mesurés, pas `isValidated` : sinon un souvenir se
     * réécrirait indéfiniment à partir de lui-même, et l'échéance de trente
     * jours ne tomberait jamais.
     *
     * Et on RELIT l'empreinte à cet instant — jamais celle du montage. Quand
     * l'élève arrive sur cet écran, il n'a encore rien autorisé : l'empreinte
     * du montage dit « non demandé ». L'enregistrer revenait à mémoriser un
     * état que la visite suivante ne retrouvait jamais, puisque l'accès y est
     * désormais accordé. Le test revenait donc à chaque fois, et le lot ne
     * servait qu'à ceux qui avaient déjà autorisé leur matériel avant.
     */
    /*
     * Les trois appareils, et pas la connexion — le souvenir ne couvre que ce
     * qu'il dispense de rejouer (voir `REMEMBERED_STEPS`). La connexion est
     * remesurée à chaque entrée quoi qu'il arrive ; l'exiger ici aussi faisait
     * qu'un test mené au bout un soir de Wi-Fi moyen n'était jamais mémorisé,
     * et l'élève le refaisait la fois suivante sans comprendre pourquoi.
     */
    const allMeasuredOk = STEPS.filter((step) => REMEMBERED_STEPS.includes(step.id)).every(
        (step) => step.verdict === STATUS.OK
    )
    const rememberedRef = useRef(false)
    useEffect(() => {
        if (!started || !allMeasuredOk || rememberedRef.current) return
        rememberedRef.current = true
        let cancelled = false
        /*
         * L'empreinte décrit les périphériques qui viennent d'être éprouvés,
         * choix de l'élève compris. Sans eux, un élève qui a trouvé son micro
         * USB par le dépannage voyait son micro intégré — celui qui ne marche
         * pas — enregistré comme vérifié.
         */
        memory.read({ audioinput: audioDeviceId, videoinput: videoDeviceId }).then((verified) => {
            if (!cancelled && verified) onVerified?.(verified)
        })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [started, allMeasuredOk])

    /*
     * Ouvrir les périphériques une fois, pour que l'étape qui s'ouvre sache
     * dans quel état ils sont. Sans ça, une caméra rouverte parce que
     * l'objectif est couvert affichait « Autoriser » — alors que l'accès est
     * accordé et que l'aperçu tourne sous les yeux de l'élève. Aucune fenêtre
     * ne surgit : les deux permissions sont accordées, c'est ce que le
     * souvenir vient de constater.
     */
    const openedForMemoryRef = useRef(false)
    useEffect(() => {
        if (!memoryLeavesWork || openedForMemoryRef.current) return
        openedForMemoryRef.current = true
        run({ request: true })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memoryLeavesWork])

    const skip = useCallback(
        (id) => {
            track(PRECALL_EVENT.RUNG_RESULT, {
                step: id,
                rung: lastRungRef.current[id] ?? null,
                result: "skipped",
            })
            setSkipped((s) => ({ ...s, [id]: true }))
        },
        [track]
    )

    // Passer n'est pas définitif : l'élève qui branche enfin son casque doit
    // pouvoir reprendre l'étape là où il l'avait laissée.
    const reopen = useCallback(
        (id) => setSkipped((s) => {
            const next = { ...s }
            delete next[id]
            return next
        }),
        []
    )

    // ---- contenu de l'étape courante ----

    // Ce que l'élève a laissé de côté sans le résoudre.
    const unresolved = STEPS.filter(
        (step) => skipped[step.id] && step.acquiredVerdict !== STATUS.OK
    )

    /**
     * Une étape repliée. Trois états, et aucun aplat de couleur.
     *
     * La checklist doit rester lisible d'un bout à l'autre du parcours : les
     * étapes à venir sont donc listées elles aussi, grisées, au lieu d'être
     * masquées. L'état se lit au pictogramme et au mot — un fond teinté par
     * ligne rendait l'écran illisible dès qu'il y avait plus d'un problème.
     */
    const renderCollapsed = (step, index) => {
        const wasSkipped = !!skipped[step.id]
        const tone =
            STEP_TONE[wasSkipped ? STATUS.BAD : step.acquiredVerdict] ?? STEP_TONE[STATUS.PENDING]

        return (
            <View
                key={step.id}
                style={{
                    paddingVertical: 14,
                    paddingHorizontal: 16,
                    // Un vert à peine perceptible sur ce qui est réglé : le
                    // parcours se voit avancer, sans que la couleur crie.
                    backgroundColor:
                        step.acquiredVerdict === STATUS.OK && !wasSkipped
                            ? theme.colors.successLightMore
                            : theme.colors.white,
                    borderTopWidth: index === 0 ? 0 : 1,
                    borderTopColor: theme.colors.lighter,
                }}
            >
                {/* Plus de pictogramme de verdict à droite : la pastille de
                    gauche porte déjà la couleur de l'état, et la phrase le dit
                    en toutes lettres. Deux signes pour une même information,
                    c'était un de trop. */}
                <StepRow step={step} tone={tone} muted>
                    {wasSkipped && (
                        <TouchableOpacity
                            onPress={() => reopen(step.id)}
                            hitSlop={8}
                            style={{ marginRight: 12 }}
                        >
                            <TextM
                                color={theme.colors.primary}
                                fontSize={12}
                                fontWeight={"Bold"}
                                style={{ textDecorationLine: "underline" }}
                            >
                                Réessayer
                            </TextM>
                        </TouchableOpacity>
                    )}
                </StepRow>
            </View>
        )
    }

    /**
     * L'accueil, avant que quoi que ce soit ne démarre.
     *
     * L'élève arrivait directement sur la première vérification, sans savoir ce
     * qui se passait ni pourquoi son navigateur lui réclamait sa caméra. Ce
     * temps d'arrêt dit ce qu'on va faire, ce qu'on va regarder et pourquoi —
     * c'est la forme que prennent Tana et Whereby avant de demander l'accès.
     *
     * Le bouton fait d'une pierre deux coups : il annonce le test, et c'est lui
     * qui autorise le micro et la caméra. La fenêtre du navigateur n'arrive
     * donc plus sans prévenir, elle répond à un clic dont l'élève connaît la
     * raison.
     */
    const renderIntro = () => (
        <View style={{ paddingVertical: 22, paddingHorizontal: 18 }}>
            {/* Où l'on est, en premier : l'élève vient d'appuyer sur le bouton
                de son live et croyait y être. Pas plus de deux lignes avant la
                liste — un écran plein de texte se lit comme un formulaire. */}
            <TextM
                fontWeight={"Bold"}
                fontSize={19}
                color={theme.colors.black}
                style={{ letterSpacing: -0.3 }}
                wrap
            >
                {copy.title}
            </TextM>
            <TextM color={theme.colors.grey} fontSize={13.5} style={{ marginTop: 8, lineHeight: 20 }} wrap>
                {/* La demande d'autorisation n'est plus annoncée ici : le
                    guidage de l'étape la dit au moment où elle apparaît. */}
                {copy.lead}
            </TextM>
            {!!memoryMissDetail(memory.reason) && (
                <TextM color={theme.colors.grey} fontSize={13.5} style={{ marginTop: 8, lineHeight: 20 }} wrap>
                    {memoryMissDetail(memory.reason)}
                </TextM>
            )}

            <View style={{ marginTop: 20 }}>
                {INTRO_ITEMS.map((item, index) => (
                    <View
                        key={item.id}
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            marginTop: index === 0 ? 0 : 14,
                        }}
                    >
                        <View
                            style={{
                                width: 30,
                                height: 30,
                                borderRadius: 9,
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: theme.colors.lighter,
                            }}
                        >
                            <FontAwesome5 name={item.icon} size={13} color={theme.colors.grey} />
                        </View>
                        <View style={{ flex: 1, marginLeft: 11 }}>
                            <TextM color={theme.colors.black} fontSize={14} fontWeight={"Bold"}>
                                {item.label}
                            </TextM>
                            <TextM color={theme.colors.light} fontSize={12} style={{ marginTop: 1 }} wrap>
                                {item.does}
                            </TextM>
                        </View>
                    </View>
                ))}
            </View>

            {/* `disabled` sur un double appui : deux `getUserMedia`
                concurrents produisent le « périphérique occupé » que cet écran
                passe justement son temps à diagnostiquer. */}
            <TouchableOpacity
                disabled={busy}
                onPress={() => {
                    setStarted(true)
                    run({ request: true })
                }}
                style={{
                    marginTop: 24,
                    backgroundColor: theme.colors.primary,
                    borderRadius: 12,
                    paddingVertical: 14,
                    alignItems: "center",
                    opacity: busy ? 0.6 : 1,
                }}
            >
                <TextM color={theme.colors.white} fontSize={15} fontWeight={"Bold"}>
                    {busy ? "Vérification…" : copy.cta}
                </TextM>
            </TouchableOpacity>
        </View>
    )

    /*
     * L'écran de celui dont rien n'a bougé.
     *
     * Il remplace l'accueil, pas le parcours : le bouton « Rejoindre » qui
     * apparaît en dessous est celui de PreCallScreen, déclenché par `allDone`.
     * Ici on dit seulement ce qui a été constaté, quand, et on laisse la porte
     * ouverte à qui veut revérifier.
     */
    const renderAlreadyVerified = ({ pending = false } = {}) => (
        <View style={{ paddingVertical: 18, paddingHorizontal: 18 }}>
            {/* Se voir vaut mieux que se l'entendre dire — et personne ne juge
                mieux que l'élève lui-même si son image est bonne.

                Sur téléphone aussi : l'avoir réservé au web ne tenait à rien,
                `CameraPreview` y sert déjà l'étape caméra. Sans lui la carte
                se réduisait à deux lignes flottant sur un écran vide.

                Il est monté DÈS l'attente, et pas seulement une fois le
                verdict réseau tombé : c'est un miroir, il n'attend le verdict
                de personne. */}
            {renderPreview({ indented: false, portrait: compact })}

            {pending ? (
                <View style={{ flexDirection: "row", alignItems: "center", paddingVertical: 4 }}>
                    <ActivityIndicator color={theme.colors.primary} size="small" />
                    <TextM color={theme.colors.grey} fontSize={13.5} style={{ marginLeft: 11 }}>
                        On vérifie…
                    </TextM>
                </View>
            ) : (
            <>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
                <View
                    style={{
                        width: 34,
                        height: 34,
                        borderRadius: 11,
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: theme.colors.successLight,
                    }}
                >
                    <FontAwesome5 name="check" size={14} color={theme.colors.success} />
                </View>
                <TextM
                    fontWeight={"Bold"}
                    fontSize={18}
                    color={theme.colors.black}
                    style={{ letterSpacing: -0.3, marginLeft: 11, flex: 1 }}
                    wrap
                >
                    {copy.remembered}
                </TextM>
            </View>

            {/* Rien d'autre que le titre.
                Ni la date du dernier test, ni l'état de la connexion, ni
                l'inventaire de ce qu'on a relu : l'élève à qui l'on annonce
                qu'il n'a rien à faire n'a pas non plus à lire pourquoi. Et une
                image de travers se dit déjà par la pastille posée sur
                l'aperçu, juste au-dessus. */}
            <TouchableOpacity
                onPress={() => setForceFull(true)}
                style={{ marginTop: 14, alignSelf: "flex-start", paddingVertical: 6 }}
            >
                <TextM color={theme.colors.primary} fontSize={13.5} fontWeight={"Bold"}>
                    Revérifier
                </TextM>
            </TouchableOpacity>
            </>
            )}
        </View>
    )

    /**
     * L'aperçu de la caméra, avec son verdict d'obstruction et sa coupure.
     *
     * Le retrait n'a de sens qu'aligné sous une étape du parcours ; sur la
     * carte « déjà vérifié », il n'y a pas d'étape au-dessus de quoi
     * s'aligner.
     */
    const renderPreview = ({ indented = true, portrait = false } = {}) => (
        <View
            ref={previewRef}
            style={{
                /*
                 * Portrait sur la carte d'entrée, et paysage dans le parcours.
                 *
                 * Un 16:9 sur un téléphone donne une bande écrasée en haut d'un
                 * écran vide — c'est ce qu'on voyait. Les applications de
                 * visio font toutes l'inverse : un grand cadre de soi qui
                 * occupe le haut, et le bouton en bas.
                 */
                aspectRatio: portrait ? 3 / 4 : 16 / 9,
                borderRadius: 14,
                overflow: "hidden",
                backgroundColor: theme.colors.black,
                marginLeft: indented ? indent : 0,
                marginBottom: 14,
            }}
        >
                {isVideoMuted ? (
                    <TouchableOpacity
                    onPress={() => onSetVideoMuted?.(false)}
                    style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
                    >
                    <FontAwesome5 name="video-slash" size={20} color={theme.colors.light} />
                    <TextM color={theme.colors.light} fontSize={11.5} style={{ marginTop: 8 }}>
                        Appuie pour activer ta vidéo
                    </TextM>
                    </TouchableOpacity>
                ) : (
                    /* L'aperçu suit l'objectif choisi : sans ça, le sélecteur
                       changeait ce qu'on sondait sans changer ce qu'on
                       montrait, et l'élève ne pouvait pas juger. */
                    <CameraPreview
                        deviceId={videoDeviceId}
                        onDeviceError={(error) => {
                            setCameraDeviceError(error)
                            // Un flux est arrivé : à partir de là, et pas
                            // avant, il y a bien une image à l'écran.
                            if (!error) setPreviewEverOpened(true)
                        }}
                    />
                )}

                {/* La caméra demandée n'a pas voulu s'ouvrir : l'image affichée
                    est encore la précédente, et il faut le dire — sinon
                    l'élève croit que son clic n'a rien fait. */}
                {!!cameraDeviceError && !isVideoMuted && (
                    <View
                        style={{
                            position: "absolute",
                            top: 11,
                            left: 11,
                            right: 11,
                            flexDirection: "row",
                            alignItems: "center",
                            backgroundColor: CAUTION,
                            borderRadius: 12,
                            paddingVertical: 7,
                            paddingHorizontal: 12,
                        }}
                    >
                        <FontAwesome5 name="exclamation" size={11} color={theme.colors.white} />
                        <TextM
                            color={theme.colors.white}
                            fontSize={11.5}
                            fontWeight={"Bold"}
                            style={{ marginLeft: 8, flex: 1 }}
                            wrap
                        >
                            {/* La cause exacte plutôt qu'une phrase unique :
                                « un autre logiciel l'utilise » et « aucune
                                caméra détectée » n'appellent pas le même
                                geste, et le dossier sait déjà les distinguer. */}
                            {previewShowsImage
                                ? `${cameraDetail(cameraDeviceError, IS_WEB)} — tu vois toujours la précédente.`
                                : cameraDetail(cameraDeviceError, IS_WEB)}
                        </TextM>
                    </View>
                )}

                {/* Une pastille posée sur l'image, pas un bandeau qui la
                    barre : le constat doit accompagner l'aperçu, pas le
                    recouvrir d'un aplat rouge. */}
                {isObstructed && (
                    <View
                    style={{
                        position: "absolute",
                        top: 11,
                        left: 11,
                        flexDirection: "row",
                        alignItems: "center",
                        backgroundColor: DANGER,
                        borderRadius: 18,
                        paddingVertical: 6,
                        paddingHorizontal: 12,
                    }}
                    >
                    <FontAwesome5 name="eye-slash" size={11} color={theme.colors.white} />
                    <TextM
                        color={theme.colors.white}
                        fontSize={11.5}
                        fontWeight={"Bold"}
                        style={{ marginLeft: 8 }}
                    >
                        Image toute noire
                    </TextM>
                    </View>
                )}

                {/* Couper sa video doit rester possible depuis l'apercu :
                    sans ce bouton, un eleve qui ne veut pas etre filme
                    n'a aucun moyen de le dire avant d'entrer. */}
                {/* Pas de « couper ma vidéo » sur un cadre vide : sans caméra,
                    la pastille proposait d'éteindre une image qui n'existe
                    pas. */}
                {!isVideoMuted && previewShowsImage && (
                    <TouchableOpacity
                    onPress={() => onSetVideoMuted?.(true)}
                    style={{
                        position: "absolute",
                        bottom: 11,
                        alignSelf: "center",
                        flexDirection: "row",
                        alignItems: "center",
                        backgroundColor: "rgba(0,0,0,0.6)",
                        borderRadius: 22,
                        paddingVertical: 7,
                        paddingHorizontal: 15,
                    }}
                    >
                    <FontAwesome5 name="video" size={12} color={theme.colors.white} />
                    <TextM
                        color={theme.colors.white}
                        fontSize={12}
                        fontWeight={"SemiBold"}
                        style={{ marginLeft: 8 }}
                    >
                        Couper ma vidéo
                    </TextM>
                    </TouchableOpacity>
                )}
            </View>
    )

    /*
     * L'aperçu repart de zéro à chaque ouverture de l'étape caméra. Sans ça,
     * « tu vois toujours la précédente » revenait au second passage sur un
     * cadre noir, la caméra ayant été débranchée entre-temps : l'image du
     * premier passage n'existe plus, le drapeau disait le contraire.
     */
    useEffect(() => {
        if (current?.id === "camera") setPreviewEverOpened(false)
    }, [current?.id])

    // ---- l'échelle et le barreau de l'étape en cours ----

    const LADDERS = { microphone: micLadder, camera: camLadder, speaker: spkLadder, network: netLadder }
    const ladder = current ? LADDERS[current.id] : null

    /*
     * Le dépannage ne commence qu'une fois qu'il y a quelque chose à réparer,
     * et qu'on sait quoi : le son une fois que l'élève a dit qu'il n'entend
     * rien, la connexion une fois mesurée, le micro une fois le délai d'écoute
     * passé, la caméra une fois que l'élève a dit qu'il ne se voit pas. Sans
     * ces gardes, le premier barreau s'affichait pendant la mesure ou avant
     * qu'on ait écouté une seule seconde.
     */
    const notYetDiagnosed =
        !!current &&
        ((current.id === "speaker" && speakerHeard !== false) ||
            (current.id === "network" && networkVerdict === STATUS.PENDING) ||
            (current.id === "microphone" && micGranted && !micHeard && !micGraceOver) ||
            (current.id === "camera" &&
                !isObstructed &&
                cameraOpened === STATUS.OK &&
                !isVideoMuted &&
                seesSelf === null))
    const rung = current && !notYetDiagnosed ? ladder.current : null
    // La ligne « Autoriser » du chemin, sous laquelle la question se dessine (Firefox n'en a pas).
    const choiceIndex = permissionChoiceIndex(browser)

    // Un barreau s'affiche : on le note, et on retient lequel pour dire plus
    // tard s'il a réglé le problème ou si l'élève a passé.
    useEffect(() => {
        if (!current || !rung) return
        lastRungRef.current[current.id] = rung.id
        track(PRECALL_EVENT.RUNG_SHOWN, { step: current.id, rung: rung.id })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current?.id, rung?.id])

    /** « Ça n'a pas marché », ou « Non » : l'essai suivant, et la mesure qui va avec. */
    const advanceLadder = (targetLadder, stepId) => {
        track(PRECALL_EVENT.RUNG_RESULT, {
            step: stepId,
            rung: targetLadder.current?.id ?? null,
            result: "next",
        })
        targetLadder.advance()
    }

    /** L'étape en cours : la seule ouverte, avec tout ce qui la concerne. */
    const renderCurrent = () => {
        if (!current) return null

        /**
         * L'étape vient de se régler, et on la retient le temps de le montrer.
         *
         * Volontairement traité *dans* l'étape et non par un rendu séparé : la
         * barre de niveau du micro doit garder sa place dans l'arbre. Sortie
         * dans un autre bloc, elle se serait démontée puis remontée — la
         * capture audio rouverte, les barres remises à plat — exactement à
         * l'instant où elles servent enfin à quelque chose.
         */
        const resolved = heldId === current.id

        // `ladder` et `rung` vivent au niveau du composant : la mesure Amplitude
        // les lit dans un effet, et ils n'ont de sens que pour l'étape en cours.

        /** Le sélecteur porte-t-il sur le micro ? `null` s'il n'y en a pas. */
        const pickAudio = rung?.picker ? rung.picker === "audioinput" : null

        // Le geste à faire, un seul. L'autorisation passe avant tout le reste :
        // sans elle, aucun autre conseil n'a de sens.
        const guidanceFor = () => {
            if (needsAuthorization && (current.id === "camera" || current.id === "microphone")) {
                return copy.needConsent
            }
            // Tant que rien ne cloche, le conseil n'est pas un dépannage :
            // c'est une invitation à se regarder. L'échelle ne sert qu'une fois
            // que l'élève a dit que quelque chose n'allait pas — sinon elle
            // proposait de vérifier le cache coulissant devant une image nette.
            if (
                current.id === "camera" &&
                !isObstructed &&
                cameraOpened === STATUS.OK &&
                !isVideoMuted &&
                seesSelf === null
            ) {
                // Pendant que l'analyse regarde, on ne demande pas encore de
                // regarder : la ligne d'état dit « je regarde ton image », le
                // conseil disait déjà « prends le temps de te regarder ». Si
                // elle tranche, l'étape se valide et ce conseil ne s'affiche
                // jamais ; la question ne reste que si elle a renoncé.
                return IS_WEB && imageSeen === IMAGE.LOOKING
                    ? "Deux secondes, je vérifie que ton image n'est pas noire."
                    : "Regarde l'image : est-ce qu'on voit bien ton visage ?"
            }
            if (current.id === "camera" && isObstructed) {
                // Le bandeau posé sur l'aperçu dit déjà que l'image est noire :
                // le répéter ici laissait trois formulations du même constat à
                // l'écran en même temps. Ne reste que le geste.
                return "Enlève ce qui cache la caméra : le petit cache, un autocollant, ou ton doigt."
            }
            // Pendant la mesure, on dit ce qu'on fait — pas ce qu'il faudrait
            // faire. Le conseil arrive avec le verdict.
            if (current.id === "network" && networkVerdict === STATUS.PENDING) {
                return "Je mesure ta connexion, deux secondes."
            }
            if (current.id === "speaker") {
                // La question et l'action vivent dans le bloc ci-dessous : le
                // conseil ne porte que l'enjeu, ou le geste quand ça a raté.
                if (speakerHeard !== false) {
                    return "Clique sur le bouton et écoute bien : tu dois entendre une petite musique."
                }
                if (rung) return rung.tip({ isWeb: IS_WEB, cause: ladder.cause, browser, audience })
                return "Tu n'entends toujours rien ? Entre quand même, et écris-le tout de suite dans le chat."
            }
            if (rung) return rung.tip({ isWeb: IS_WEB, cause: ladder.cause, browser, audience })

            // L'échelle est épuisée : il faut une fin, et elle doit dire la
            // vérité. Sans elle, le dernier clic ne produisait rien et l'écran
            // reproposait son conseil d'ouverture, comme si on repartait à zéro.
            if (ladder.total > 0 && ladder.position >= ladder.total) {
                if (current.id === "camera") {
                    return copy.cameraGiveUp
                }
                if (current.id === "microphone") {
                    // Aucune promesse d'aide extérieure : voir le bilan. On ne
                    // dit à l'élève que ce qui est vrai sans dépendre de
                    // personne.
                    return "Ça ne marche toujours pas ? Entre quand même : tu pourras écrire dans le chat, et redire « allô » pendant le live."
                }
                return "On ne peut pas réparer le Wi-Fi d'ici. Entre quand même, et coupe ta vidéo si ça saccade."
            }

            if (current.id === "camera") return "Regarde l'image : est-ce que tu te vois bien ?"
            return "Dis « allô » — les barres doivent bouger."
        }

        const guidance = guidanceFor()

        // La seule source de vérité sur « quels gestes propose-t-on ». Le rendu
        // n'en décide plus : il affiche ce que la règle, testée à part sur
        // toutes les combinaisons, lui donne.
        const actions = visibleActions({
            stepId: current.id,
            needsAuthorization,
            isWeb: IS_WEB,
            isVideoMuted,
            isObstructed,
            image: imageSeen,
            soundPlayed,
            speakerHeard,
            seesSelf,
            rungId: rung?.id ?? null,
            /*
             * Le dernier barreau a droit à son « Ça n'a pas marché ».
             *
             * Avec `<`, le bouton disparaissait sur le dernier essai :
             * `advance()` n'était plus appelable, `ladder.current` ne devenait
             * jamais `null`, et la fin d'échelle — « Si ça ne marche toujours
             * pas, entre quand même… » — était inatteignable sur le micro, le
             * réseau et la caméra sans flux. `position` est plafonnée à `total`
             * par le hook, et `NEXT_ATTEMPT` exige un barreau courant : le
             * bouton s'efface donc bien une fois l'échelle épuisée.
             */
            hasMoreAttempts: ladder.position <= ladder.total,
            micListening: askToSpeak,
            cameraReady: cameraOpened === STATUS.OK,
        })
        const offers = (action) => actions.includes(action)

        /**
         * On attend que l'élève parle : il faut le lui dire, pas le lui écrire.
         *
         * La consigne existait, mais noyée dans la bulle de guidage, au même
         * rang que « regarde l'interrupteur de ton casque » — et écrite lettre
         * par lettre, donc lisible seulement après plusieurs secondes. Le seul
         * geste attendu de lui était le moins visible de l'écran.
         *
         * Elle sort donc de la bulle : une consigne courte, en grand, collée
         * aux barres qui lui répondront. C'est la forme du test du son, qui
         * fonctionne déjà — une pastille, un mot, une mesure qui bouge.
         */
        return (
            /* Fond neutre, et un filet d'accent à gauche pour dire « c'est ici
               que ça se passe ». La couleur d'état ne colore que la ligne de
               constat : teinter toute la carte faisait disparaître la
               hiérarchie dès qu'un problème apparaissait. */
            <View
                style={{
                    // Le seul fond teinté de la liste, et c'est ce qui rend
                    // l'étape en cours reconnaissable au premier coup d'œil.
                    // Vert le temps de la réussite : c'est le seul instant où
                    // la couleur d'état a le droit de prendre toute la carte,
                    // parce qu'il n'y a plus rien d'autre à y lire.
                    backgroundColor: resolved
                        ? theme.colors.successLightMore
                        : theme.colors.primaryLighter,
                    borderTopWidth: currentIndex === 0 ? 0 : 1,
                    borderTopColor: theme.colors.lighter,
                    // Bleu = « c'est ici que ça se passe ». L'état, lui, vit
                    // dans l'icône : mélanger les deux donnait un filet
                    // brunâtre qui ne disait ni l'un ni l'autre.
                    borderLeftWidth: 3,
                    borderLeftColor: resolved ? theme.colors.success : theme.colors.primary,
                    paddingVertical: 16,
                    // 13 + le filet de 3 px = les 16 px des autres lignes : les
                    // icônes tombent à la même verticale d'un bout à l'autre.
                    paddingLeft: 13,
                    paddingRight: 16,
                }}
            >
                {/* Plus de « ÉTAPE n SUR 4 » ici : l'avancement est déjà dit
                    dans l'en-tête de la carte, et la liste des quatre étapes est
                    sous les yeux. Trois compteurs numériques cohabitaient, tous
                    « sur 4 », tous en petites capitales grises — on ne savait
                    plus lequel parlait de quoi. */}
                <StepRow
                    step={current}
                    tone={STEP_TONE[current.acquiredVerdict] ?? STEP_TONE[STATUS.PENDING]}
                />

                {/* L'aperçu vit dans l'étape caméra, juste au-dessus de son
                    conseil. C'est ce qui rend le guidage compréhensible :
                    parler d'une image affichée ailleurs ne veut rien dire. */}
                {current.id === "camera" && renderPreview()}

                {/* Pendant la pause de réussite, le constat remplace le
                    guidage : plus un seul conseil, plus une seule question. */}
                {/* Le bouton d'abord, les conseils ensuite.
                    Le test vivait sous l'échelle de dépannage : l'élève lisait
                    trois conseils avant de trouver le bouton qui, dans neuf cas
                    sur dix, réglait tout d'un clic. On remonte donc l'action en
                    tête de l'étape, et ce qui l'explique passe dessous.

                    Trois temps plutôt que trois boutons, en revanche, on garde :
                    « Écouter » et « Je l'ai entendu » côte à côte se lisaient
                    comme un choix binaire, alors que l'un déclenche et l'autre
                    répond. La pastille agit, la question ne se pose qu'une fois
                    le son joué. */}
                {!resolved && current.id === "speaker" && (
                    <View style={{ marginTop: 12, marginLeft: indent }}>
                        <View style={{ flexDirection: "row", alignItems: "center" }}>
                            <TouchableOpacity
                                onPress={async () => {
                                    chimeRun.current += 1
                                    const run = chimeRun.current
                                    setSoundPlayed(true)
                                    setSoundBlocked(false)
                                    setChiming(true)
                                    const sound = await PlaySoundM.play(
                                        require("@assets/sounds/confirmation_002.mp3")
                                    )
                                    const played = await playbackStarted(sound, CHIME_MS)
                                    if (chimeRun.current !== run) return
                                    setChiming(false)
                                    setSoundBlocked(!played)
                                }}
                                style={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: 26,
                                    backgroundColor: theme.colors.primary,
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <FontAwesome5 name="volume-up" size={22} color={theme.colors.white} />
                            </TouchableOpacity>
                            <View style={{ marginLeft: 14, flex: 1 }}>
                                {/* La consigne d'ouverture porte le même poids
                                    que « Dis allô » : sur chaque étape, ce qu'on
                                    attend de l'élève se lit en premier et se lit
                                    plus gros. Une fois le son joué, la phrase
                                    n'est plus une consigne mais un état, et elle
                                    reprend sa taille. */}
                                <TextM
                                    color={
                                        soundBlocked
                                            ? CAUTION
                                            : soundPlayed || chiming
                                                ? theme.colors.grey
                                                : theme.colors.black
                                    }
                                    fontSize={soundBlocked || soundPlayed || chiming ? 13 : 16}
                                    fontWeight={
                                        soundBlocked || soundPlayed || chiming ? "SemiBold" : "Bold"
                                    }
                                    style={
                                        soundBlocked || soundPlayed || chiming
                                            ? undefined
                                            : { lineHeight: 22, letterSpacing: -0.1 }
                                    }
                                    wrap
                                >
                                    {/* Le cas où l'élève n'y est pour rien : ce
                                        n'est ni son volume ni son casque, c'est
                                        la lecture qui a été refusée. Lui
                                        conseiller de monter le son serait
                                        l'envoyer chercher là où il n'y a rien.
                                        Et comme partout ailleurs : le
                                        navigateur sur ordinateur, l'appareil
                                        sur téléphone. */}
                                    {soundBlocked
                                        ? `${IS_WEB ? browserLabel(browser) : "Ton appareil"} a bloqué le son. ${IS_WEB ? "Clique" : "Appuie"} encore une fois : ça marche presque toujours.`
                                        : chiming
                                            ? "Ça joue…"
                                            : soundPlayed
                                                ? `${IS_WEB ? "Clique" : "Appuie"} à nouveau pour réécouter.`
                                                : `${IS_WEB ? "Clique" : "Appuie"} sur le bouton.`}
                                </TextM>
                                <View style={{ marginTop: 7 }}>
                                    <OutputLevel playing={chiming} />
                                </View>
                            </View>
                        </View>

                        {offers(ACTION.HEARD_YES) && (
                            <View style={{ marginTop: 16 }}>
                                <TextM color={theme.colors.black} fontSize={14} fontWeight={"Bold"} wrap>
                                    Tu as entendu la mélodie ?
                                </TextM>
                                <View style={[buttonRow(compact), { marginTop: 10 }]}>
                                    <StepButton
                                        block={compact}
                                        filled
                                        tone={theme.colors.primary}
                                        label="Oui, j'entends !"
                                        onPress={() => setSpeakerHeard(true)}
                                    />
                                    {/* « Non » est le bouton « essai suivant » :
                                        un second bouton « Ça n'a pas marché »
                                        disait exactement la même chose. */}
                                    <StepButton
                                        block={compact}
                                        tone={theme.colors.primary}
                                        label="Non, je n'entends rien"
                                        onPress={() => {
                                            if (speakerHeard === false) advanceLadder(spkLadder, "speaker")
                                            else setSpeakerHeard(false)
                                        }}
                                    />
                                </View>
                            </View>
                        )}
                    </View>
                )}

                {resolved ? (
                    <Animated.View
                        entering={BounceIn.duration(360)}
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            marginLeft: indent,
                            marginTop: 14,
                        }}
                    >
                        <FontAwesome5 name="check" size={13} color={theme.colors.successDark} />
                        <TextM
                            color={theme.colors.successDark}
                            fontSize={15}
                            fontWeight={"Bold"}
                            style={{ marginLeft: 10, flex: 1 }}
                            wrap
                        >
                            {RESOLVED_TEXT[current.id]}
                        </TextM>
                    </Animated.View>
                ) : askToSpeak ? (
                    /* La même bulle que partout ailleurs, au pixel près : ce
                       n'est pas la couleur qui doit distinguer la consigne des
                       conseils, c'est la place qu'elle occupe dans la phrase.
                       Une carte pleine criait — et criait précisément là où
                       l'écran veut rassurer. */
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            marginLeft: indent,
                            marginTop: 14,
                            backgroundColor: theme.colors.white,
                            borderRadius: 14,
                            borderWidth: 1,
                            borderColor: theme.colors.lighter,
                            paddingVertical: 13,
                            paddingHorizontal: 15,
                        }}
                    >
                        {/* Le même badge de 24 px que les gestes du guidage, à
                            un souffle près : il respire tant qu'on écoute, et
                            c'est le seul mouvement de l'étape. */}
                        <Animated.View
                            style={[
                                {
                                    width: 24,
                                    height: 24,
                                    borderRadius: 7,
                                    backgroundColor: theme.colors.primaryLighter,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginRight: 11,
                                },
                                pulseStyle,
                            ]}
                        >
                            <FontAwesome5 name="microphone" size={11} color={theme.colors.primary} />
                        </Animated.View>
                        <View style={{ flex: 1 }}>
                            <TextM
                                color={theme.colors.black}
                                fontSize={16}
                                fontWeight={"Bold"}
                                style={{ lineHeight: 22, letterSpacing: -0.1 }}
                                wrap
                            >
                                Dis « allô » à voix haute
                            </TextM>
                            <TextM
                                color={theme.colors.light}
                                fontSize={12.5}
                                style={{ marginTop: 2, lineHeight: 17 }}
                                wrap
                            >
                                Je t'écoute : les barres en dessous vont bouger.
                            </TextM>
                        </View>
                    </View>
                ) : (
                    <TypedGuidance
                        lines={guidance}
                        dotColor={DOT_COLOR[current.acquiredVerdict] ?? DOT_COLOR[STATUS.PENDING]}
                        /* Sous la ligne « Choisis Autoriser » : ce que l'élève va
                           voir s'ouvrir, sans dire où — les navigateurs déplacent
                           leurs boutons. Pas sur téléphone, où le chemin passe
                           par les Réglages. */
                        illustration={
                            IS_WEB && rung?.id === "permission" && choiceIndex != null
                                ? {
                                    afterIndex: choiceIndex,
                                    node: (
                                        <PermissionHint
                                            device={current.id === "camera" ? "Caméra" : "Micro"}
                                        />
                                    ),
                                }
                                : null
                        }
                    />
                )}

                {/* L'icône montrée, une fois le conseil lu — et du bon côté.
                    Sur téléphone il n'y a pas de barre d'adresse, et sur Safari
                    rien n'apparaît dedans : dans les deux cas le guidage passe
                    par un menu, et ce schéma n'aurait rien à désigner. */}
                {/* Volontairement NON conditionné par `resolved` : c'est
                    précisément pendant la pause de réussite que ces barres
                    doivent rester à l'écran. Elles disparaissaient avec
                    l'étape à l'instant où elles prouvaient que le micro
                    marchait — le seuil de détection étant bas, l'étape pouvait
                    se valider au premier bruit de la pièce et l'élève ne les
                    voyait jamais bouger. Il peut maintenant continuer de
                    parler et regarder. */}
                {current.id === "microphone" && micGranted && (
                    <View style={{ marginTop: 11, marginLeft: indent }}>
                        <MicrophoneLevel deviceId={audioDeviceId} onHeard={handleMicHeard} />
                    </View>
                )}

                {/* Les seuls boutons restants : les gestes que l'app ne peut pas
                    faire a la place de l'eleve. Le reste se revérifie tout seul.
                    Rien pendant la pause de réussite : on ne redemande pas un
                    geste sur une étape qui vient d'aboutir. */}
                <View
                    style={[
                        buttonRow(compact),
                        { marginLeft: indent, marginTop: 12, display: resolved ? "none" : "flex" },
                    ]}
                >
                    {/* La sortie de l'écoute : elle coupe court au délai et
                        ouvre le dépannage tout de suite. Formulée sur ce que
                        l'élève constate — les barres — et non sur un diagnostic
                        qu'on lui demanderait de porter. */}
                    {offers(ACTION.MIC_NO_SIGNAL) && (
                        <StepButton
                            block={compact}
                            tone={theme.colors.primary}
                            label="Je ne vois rien bouger"
                            onPress={() => setMicGraceOver(true)}
                        />
                    )}
                    {/* Le geste que l'élève devrait faire au cadenas, tenté
                        pour lui : si le refus n'a duré que la session, la
                        fenêtre revient et il n'a rien d'autre à faire. */}
                    {/* Le troisième pas du conseil, en un clic. */}
                    {/* Sur téléphone il n'y a ni cadenas ni rechargement :
                        l'écran des réglages est le seul endroit où l'accès se
                        rend, et on y emmène l'élève directement. */}
                    {offers(ACTION.OPEN_SETTINGS) && (
                        <StepButton
                            block={compact}
                            filled
                            icon="cog"
                            tone={theme.colors.primary}
                            label="Ouvrir les réglages"
                            onPress={() => Linking.openSettings().catch(() => {})}
                        />
                    )}
                    {offers(ACTION.AUTHORIZE) && (
                        <StepButton
                            block={compact}
                            filled
                            icon="unlock"
                            tone={theme.colors.primary}
                            label={busy ? "Vérification…" : "Autoriser"}
                            disabled={busy}
                            onPress={() => run({ request: true })}
                        />
                    )}
                    {/* Le conseil réseau dit de couper la vidéo — encore
                        faut-il pouvoir le faire : l'aperçu et son bouton ne
                        vivent qu'à l'étape caméra. */}
                    {offers(ACTION.MUTE_VIDEO) && (
                        <StepButton
                            block={compact}
                            filled
                            tone={theme.colors.primary}
                            label="Couper ma vidéo"
                            onPress={() => onSetVideoMuted?.(true)}
                        />
                    )}
                    {offers(ACTION.ENABLE_VIDEO) && (
                        <StepButton
                            block={compact}
                            filled
                            icon="video"
                            tone={theme.colors.primary}
                            label="Activer ma vidéo"
                            onPress={() => onSetVideoMuted?.(false)}
                        />
                    )}
                    {/* La question n'est posée qu'à qui n'a pas d'analyse pour y
                        répondre : sur mobile, ou sur le web quand l'analyse a
                        renoncé faute d'image lisible. Une image lue et non
                        noire valide l'étape sans rien demander, comme le micro
                        au premier son. Les deux réponses existent : sans la
                        négative, celui qui ne se voit pas n'avait que
                        « Passer », et l'échelle de dépannage restait
                        inatteignable. */}
                    {/* Tant que l'image est noire, on ne demande rien : l'écran
                        vient d'annoncer qu'elle l'est, proposer « oui je me vois
                        bien » à côté serait une question dont on connaît déjà la
                        réponse — et un moyen de valider une caméra masquée.
                        L'élève découvre l'objectif, l'analyse le voit en deux
                        secondes, et la question arrive ensuite. */}
                    {offers(ACTION.SEES_SELF_YES) && (
                        <>
                            <StepButton
                                block={compact}
                                filled
                                tone={theme.colors.primary}
                                label="Oui, je me vois bien"
                                onPress={() => setSeesSelf(true)}
                            />
                            <StepButton
                                block={compact}
                                tone={theme.colors.primary}
                                label={seesSelf === false ? "Toujours pas" : "Non, je ne me vois pas"}
                                onPress={() => {
                                    if (seesSelf === false) advanceLadder(camLadder, "camera")
                                    else setSeesSelf(false)
                                }}
                            />
                        </>
                    )}
                </View>

                {!resolved && rung && rung.picker && pickAudio !== null && (
                    <View style={{ marginTop: 9, marginLeft: indent }}>
                        {(pickAudio ? devices.audioinput : devices.videoinput).map(
                            (device, index) => {
                                const selectedId = pickAudio ? audioDeviceId : videoDeviceId
                                const isSelected = selectedId ? device.deviceId === selectedId : index === 0
                                return (
                                    <TouchableOpacity
                                        key={device.deviceId || index}
                                        onPress={() => {
                                            ;(pickAudio ? setAudioDeviceId : setVideoDeviceId)(
                                                device.deviceId
                                            )
                                            /*
                                             * Micro seulement, jamais la caméra.
                                             *
                                             * C'était le seul `run` du fichier sans
                                             * `only`, donc il demandait audio ET vidéo :
                                             * il rouvrait l'objectif que l'aperçu était
                                             * en train de rouvrir dans le même tick — deux
                                             * captures concurrentes sur une webcam qui n'en
                                             * accepte souvent qu'une, d'où un « périphérique
                                             * occupé » et une ligne rouge devant une image
                                             * parfaite. Et à l'étape micro, il allumait la
                                             * webcam pour rien.
                                             *
                                             * Pour la caméra il n'y a rien à sonder :
                                             * l'aperçu ouvre l'objectif choisi et rapporte
                                             * lui-même son échec.
                                             */
                                            if (pickAudio) {
                                                run({
                                                    request: true,
                                                    only: "microphone",
                                                    audioDeviceId: device.deviceId,
                                                    videoDeviceId,
                                                })
                                            }
                                        }}
                                        style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            borderWidth: 1,
                                            borderColor: isSelected ? theme.colors.primary : theme.colors.lightMore,
                                            backgroundColor: theme.colors.white,
                                            borderRadius: 10,
                                            paddingVertical: 7,
                                            paddingHorizontal: 10,
                                            marginBottom: 4,
                                        }}
                                    >
                                        <FontAwesome5
                                            name={isSelected ? "dot-circle" : "circle"}
                                            size={11}
                                            color={isSelected ? theme.colors.primary : theme.colors.light}
                                            style={{ marginRight: 8 }}
                                        />
                                        <TextM
                                            color={isSelected ? theme.colors.primary : theme.colors.grey}
                                            fontSize={11.5}
                                            style={{ flex: 1 }}
                                            wrap
                                        >
                                            {device.label || `${pickAudio ? "Micro" : "Caméra"} ${index + 1}`}
                                        </TextM>
                                    </TouchableOpacity>
                                )
                            }
                        )}
                    </View>
                )}

                {/* Ni « Ça n'a pas marché » ni « Passer » sur une étape qui
                    vient de réussir : proposer d'abandonner une réussite n'a
                    aucun sens, et ça la ferait douter. */}
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 12,
                        marginLeft: indent,
                        display: resolved ? "none" : "flex",
                    }}
                >
                    {/* « Ça n'a pas marché » est une action, pas une note de
                        bas de page : trois liens minuscules alignés ne se
                        distinguaient plus les uns des autres. */}
                    {/* Seulement là où l'élève ne donne pas de verdict. Sur le
                        son et la caméra, c'est « Non » qui demande l'essai
                        suivant — deux boutons pour la même intention, c'était un
                        de trop. */}
                    {/* La caméra retrouve « Ça n'a pas marché » pendant une
                        obstruction : c'est le seul moment de cette étape où
                        l'élève n'a pas de bouton « Non » sous la main, puisqu'on
                        ne lui pose pas la question. */}
                    {offers(ACTION.NEXT_ATTEMPT) ? (
                        <StepButton
                            tone={theme.colors.primary}
                            label="Ça n'a pas marché"
                            onPress={() => advanceLadder(ladder, current.id)}
                        />
                    ) : (
                        <View />
                    )}
                    {/* La sortie de l'étape, en gris lisible : pas un bouton,
                        l'action est à gauche. « Entrer sans finir », la
                        sortie de l'écran, vit sous la carte. */}
                    <View style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap" }}>
                        <TouchableOpacity
                            onPress={() => skip(current.id)}
                            hitSlop={12}
                            style={{ paddingVertical: 8, paddingHorizontal: 6 }}
                        >
                            <TextM
                                color={theme.colors.grey}
                                fontSize={13}
                                fontWeight={"SemiBold"}
                                style={{ textDecorationLine: "underline" }}
                            >
                                Passer cette étape
                            </TextM>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        )
    }

    // Les quatre étapes restent listées de bout en bout — c'est la checklist,
    // et la masquer privait l'élève de toute idée du chemin restant. Une seule
    // est dépliée : celle dont c'est le tour. Les suivantes sont grisées et
    // muettes, donc visibles sans être un appel à l'action.

    /**
     * Avancer et réussir sont deux choses, et la barre les confondait.
     *
     * Elle se remplissait en vert sur `isValidated`, qui compte aussi les
     * étapes passées : quatre clics sur « Passer » affichaient « 4 sur 4 » en
     * vert au-dessus d'un bilan annonçant qu'il restait tout à régler.
     *
     * Deux mesures, donc, et deux couleurs. Le vert ne dit que le réglé ; ce
     * qui a été laissé de côté avance quand même la barre, en ambre, parce que
     * l'élève a bel et bien progressé dans le parcours — mais ça ne se
     * déguise plus en réussite.
     */
    const resolvedCount = STEPS.filter((step) => step.acquiredVerdict === STATUS.OK).length
    const settledCount = STEPS.filter(isValidated).length
    const allResolved = resolvedCount === STEPS.length

    const progress = useSharedValue(0)
    const settledProgress = useSharedValue(0)
    useEffect(() => {
        progress.value = withTiming(resolvedCount / STEPS.length, { duration: 480 })
        settledProgress.value = withTiming(settledCount / STEPS.length, { duration: 480 })
    }, [progress, settledProgress, resolvedCount, settledCount, STEPS.length])
    const progressStyle = useAnimatedStyle(() => ({ width: `${progress.value * 100}%` }))
    const settledStyle = useAnimatedStyle(() => ({ width: `${settledProgress.value * 100}%` }))

    const hasTrouble = unresolved.length > 0

    /*
     * Une seule couleur, et seulement sur la pastille.
     *
     * Teinter le fond, le cadre, le titre, le texte et les boutons de la même
     * couleur d'état fait virer tout l'écran au rouge dès qu'un point n'est pas
     * réglé, et plus rien n'en ressort — ni le titre, ni les boutons, ni ce
     * qu'il faut faire. Le fond reste blanc, le titre garde la couleur du
     * texte, les boutons celle de la marque, et la couleur d'état ne subsiste
     * que là où elle informe.
     */
    const summary = hasTrouble
        ? {
            icon: "exclamation",
            accent: CAUTION,
            wash: theme.colors.warningLight,
            title:
                unresolved.length > 1
                    ? "Il reste quelques points à régler"
                    : "Il reste un point à régler",
            /*
             * Aucune promesse d'aide extérieure. L'équipe est bien prévenue —
             * n'importe quel voyant non vert la prévient — mais le dire à
             * l'élève reviendrait à lui faire attendre quelqu'un, et c'est
             * précisément l'engagement qu'on a retiré.
             */
            text: "Tu peux entrer quand même. Tu pourras réessayer pendant le live.",
        }
        : {
            icon: "check",
            accent: theme.colors.success,
            wash: theme.colors.successLight,
            title: "Tout est prêt",
            text: "On te voit, on t'entend, ta connexion est bonne. Bon live !",
        }

    return (
        <View style={style}>
            <View
                style={{
                    backgroundColor: theme.colors.white,
                    borderRadius: 18,
                    borderWidth: 1,
                    borderColor: theme.colors.lightMore,
                    overflow: "hidden",
                }}
            >
                {journeyVisible ? (
                    <>
                    {/* Le décompte revient, mais rattaché au titre et dit en mots.
                        Quatre segments flottants au-dessus de la liste répétaient
                        ce que la liste montrait déjà ; « 2 sur 4 » ajoute une
                        information que la liste ne donne pas — combien il reste.
                        C'est ce que font Outseta, Adaline, Uvodo et Remote. */}
                    <View
                        style={{
                            paddingHorizontal: 16,
                            paddingTop: 15,
                            borderBottomWidth: 1,
                            borderBottomColor: theme.colors.lighter,
                        }}
                    >
                        <View style={{ flexDirection: "row", alignItems: "baseline" }}>
                            <TextM
                                fontWeight={"Bold"}
                                fontSize={16}
                                color={theme.colors.black}
                                style={{ flex: 1, letterSpacing: -0.2 }}
                            >
                                {copy.checking}
                            </TextM>
                            {/* Le décompte ne compte que le réglé, et il ne
                                passe au vert que si tout l'est vraiment. Sur
                                `settledCount`, un élève qui passait ses quatre
                                étapes lisait « 4 sur 4 » en vert. */}
                            <TextM
                                color={allResolved ? theme.colors.successDark : theme.colors.light}
                                fontSize={12.5}
                                fontWeight={"Bold"}
                            >
                                {`${resolvedCount} sur ${STEPS.length}`}
                            </TextM>
                        </View>
                        <TextM color={theme.colors.light} fontSize={12.5} style={{ marginTop: 3 }} wrap>
                            {copy.promise}
                        </TextM>

                        {/* La barre court le long du bord bas de l'en-tête, comme
                            chez Uvodo : elle appartient à la carte au lieu de
                            flotter au-dessus d'elle. */}
                        <View
                            style={{
                                height: 3,
                                borderRadius: 2,
                                backgroundColor: theme.colors.lighter,
                                marginTop: 14,
                                overflow: "hidden",
                            }}
                        >
                            {/* Dessous, l'avancement — étapes passées comprises,
                                en ambre. Dessus, le vert, qui ne dit que le
                                réglé. Une étape laissée de côté fait donc
                                avancer la barre sans la verdir. */}
                            <Animated.View
                                style={[
                                    {
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        height: "100%",
                                        borderRadius: 2,
                                        backgroundColor: CAUTION,
                                        opacity: 0.45,
                                    },
                                    settledStyle,
                                ]}
                            />
                            <Animated.View
                                style={[
                                    {
                                        height: "100%",
                                        borderRadius: 2,
                                        backgroundColor: theme.colors.success,
                                    },
                                    progressStyle,
                                ]}
                            />
                        </View>
                    </View>

                    {STEPS.map((step, index) =>
                        index === currentIndex ? (
                            <View key={step.id}>{renderCurrent()}</View>
                        ) : (
                            renderCollapsed(step, index)
                        )
                    )}
                    </>
                ) : probing ? (
                    /* Le temps de la vérification silencieuse. Sans cette
                       attente, l'accueil s'affiche puis se fait remplacer par
                       la carte : l'élève voit clignoter l'écran qu'on
                       s'apprêtait justement à lui épargner. */
                    <View style={{ paddingVertical: 48, alignItems: "center" }}>
                        <ActivityIndicator color={theme.colors.primary} />
                    </View>
                ) : memoryApplies ? (
                    renderAlreadyVerified({ pending: memoryVerdictsPending })
                ) : (
                    renderIntro()
                )}
            </View>

            {/* Le bilan est une carte À PART, détachée de la précédente.
                Collé sous la dernière étape et teinté comme elle, il se lisait
                comme un prolongement de cette étape — alors qu'il parle de
                l'ensemble du test et annonce ce qui va se passer ensuite.

                Il ne paraît pas derrière la carte « déjà vérifié » : celle-ci
                dit déjà l'essentiel, et le bilan y ajouterait « on te voit, on
                t'entend » — une mesure qu'on n'a justement pas refaite
                aujourd'hui. Une promesse de trop est exactement ce que ce
                raccourci doit s'interdire. */}
            {!current && journeyVisible && (
                <View
                    style={{
                        marginTop: 12,
                        borderRadius: 18,
                        borderWidth: 1,
                        borderColor: theme.colors.lightMore,
                        backgroundColor: theme.colors.white,
                        paddingVertical: 18,
                        paddingHorizontal: 18,
                    }}
                >
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                        {/* Le seul aplat de couleur de la carte, et donc le
                            point où le regard se pose en premier. */}
                        <Animated.View
                            entering={BounceIn.duration(520)}
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: 21,
                                backgroundColor: summary.wash,
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <FontAwesome5 name={summary.icon} size={17} color={summary.accent} />
                        </Animated.View>
                        <TextM
                            fontWeight={"Bold"}
                            fontSize={16.5}
                            color={theme.colors.black}
                            style={{ marginLeft: 13, flex: 1, letterSpacing: -0.2 }}
                            wrap
                        >
                            {summary.title}
                        </TextM>
                    </View>

                    <TextM
                        color={theme.colors.grey}
                        fontSize={13.5}
                        style={{ marginTop: 11, lineHeight: 20 }}
                        wrap
                    >
                        {summary.text}
                    </TextM>

                    {/* Ce qui reste, nommé ligne par ligne plutôt que fondu dans
                        une phrase : l'élève voit ce dont on parle, et chaque
                        ligne porte son propre bouton de reprise. */}
                    {hasTrouble && (
                        <View style={{ marginTop: 14 }}>
                            {unresolved.map((step) => (
                                <View
                                    key={step.id}
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        borderTopWidth: 1,
                                        borderTopColor: theme.colors.lighter,
                                        paddingVertical: 11,
                                    }}
                                >
                                    <FontAwesome5
                                        name={step.icon}
                                        size={12}
                                        color={summary.accent}
                                        style={{ width: 18 }}
                                    />
                                    <TextM
                                        color={theme.colors.black}
                                        fontSize={13.5}
                                        fontWeight={"SemiBold"}
                                        style={{ flex: 1, marginLeft: 8 }}
                                    >
                                        {step.label}
                                    </TextM>
                                    <TouchableOpacity onPress={() => reopen(step.id)} hitSlop={8}>
                                        <TextM
                                            color={theme.colors.primary}
                                            fontSize={12.5}
                                            fontWeight={"Bold"}
                                            style={{ textDecorationLine: "underline" }}
                                        >
                                            Réessayer
                                        </TextM>
                                    </TouchableOpacity>
                                </View>
                            ))}
                        </View>
                    )}
                </View>
            )}
        </View>
    )
}

export default PreCallCheckPanel
